/**
 * travelRouteApi — dünner httpsCallable-Wrapper der `travelRouteDistance`-Function
 * (RK-21, architecture-reisekosten.md §14.3/§14.5 V21).
 *
 * Muster: `src/api/advisorInviteApi.ts` (httpsCallable via `firebase/functions`, Region
 * `europe-west3` steckt bereits in der `functions`-Instanz aus `firebaseConfig.ts`). Der
 * Callable wird bewusst LAZY innerhalb der Funktion gebunden (NICHT auf Modul-Ebene wie in
 * `apiKeysApi.ts`), damit der Import dieser Datei keinen Seiteneffekt hat — relevant, weil
 * `TripMileageForm`/`TravelExpensesPage` diese Datei auch importieren, wenn das Route-Lookup-
 * Feature-Flag AUS ist (Null-Delta-Pfad, §14.6).
 *
 * V21 (❌, §14.5): der ORS-API-Key existiert AUSSCHLIESSLICH als Function-Secret — diese Datei
 * kennt nur den Callable-Namen `travelRouteDistance`, nie einen Schlüssel.
 * V25 (§14.5): keine Adress-Inhalte werden geloggt — diese Datei loggt gar nichts (der
 * technische Fehler wird an den Aufrufer durchgereicht, der ihn ggf. selbst loggt).
 *
 * Eigenes Fehler-Vokabular ({@link TravelRouteLookupError}/{@link TravelRouteLookupErrorCode}),
 * bewusst GETRENNT von `api/travelApiError.ts`'s `TravelApiError` (architecture-reisekosten.md
 * §14.3): Route-Lookup-Fehler (`notConfigured`, `geocodeNoResult`, `quotaExceeded`, …) gehören
 * zu einem eigenständigen externen-API-Vertrag mit eigenen i18n-Texten
 * (`mileage.lookupErrors.*`, §14.4) — eine Wiederverwendung von `TravelApiErrorCode` (das für
 * `travelApi.ts`s CRUD-Fehler steht: `notAuthenticated`/`validationFailed`/…) würde beide
 * Vokabulare vermischen und `formatTravelError`s `apiErrors.<code>`-Mapping mit fremden,
 * nicht in `TravelApiErrorCode` enthaltenen Werten kollidieren lassen.
 *
 * @module travelRouteApi
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

/**
 * Fehler-Codes des Route-Lookups (architecture-reisekosten.md §14.3), identisch zu
 * `functions/src/travel/routeDistance.ts`s `RouteDistanceErrorCode` PLUS `'network'` —
 * `'network'` entsteht erst client-seitig, wenn die Callable selbst nicht erreichbar ist bzw.
 * ein nicht erkannter/nicht gesetzter Fehler-Code zurückkommt (fail-closed Default).
 */
export type TravelRouteLookupErrorCode =
  | 'notConfigured'
  | 'invalidInput'
  | 'geocodeNoResult'
  | 'routeNotFound'
  | 'quotaExceeded'
  | 'upstreamError'
  | 'network';

const KNOWN_ROUTE_LOOKUP_ERROR_CODES: readonly TravelRouteLookupErrorCode[] = [
  'notConfigured',
  'invalidInput',
  'geocodeNoResult',
  'routeNotFound',
  'quotaExceeded',
  'upstreamError',
];

/**
 * Typisierter Fehler des Route-Lookups. `message` bleibt der technische Roh-String (Logs) —
 * das UI liest ausschließlich `code` (+ optional `field`) über die `mileage.lookupErrors.<code>`
 * i18n-Texte (§14.4), NIE `message` direkt (V23-Analogie).
 */
export class TravelRouteLookupError extends Error {
  readonly code: TravelRouteLookupErrorCode;
  /** Bei `code === 'geocodeNoResult'`: welches der beiden Adressfelder betroffen war. */
  readonly field?: 'origin' | 'destination';

  constructor(code: TravelRouteLookupErrorCode, message: string, field?: 'origin' | 'destination') {
    super(message);
    // ES5-Downlevel-Falle (tsconfig target: es5): `extends Error` verliert ohne diesen Fix
    // die Prototype-Chain, `instanceof TravelRouteLookupError` würde sonst nach der
    // Transpilation fehlschlagen (Muster `TravelApiError`/`BankImportError`).
    Object.setPrototypeOf(this, TravelRouteLookupError.prototype);
    this.name = 'TravelRouteLookupError';
    this.code = code;
    this.field = field;
  }
}

/** Erfolgs-Ergebnis eines Distanz-Lookups (architecture-reisekosten.md §14.3, spiegelt `functions/src/travel/routeDistance.ts`s `RouteDistanceResult`). */
export interface TravelRouteLookupResult {
  /** Einfachstrecke in Metern (Integer). Verdopplung/Rundung passiert im FE-Helper `buildRoundTripKmSuggestion` (E15) — NICHT hier. */
  oneWayMeters: number;
  /** Geocodiertes Start-Label (Transparenz — der Nutzer sieht, WAS aufgelöst wurde). */
  originLabel: string;
  /** Geocodiertes Ziel-Label. */
  destinationLabel: string;
}

/** Server-Payload der `travelRouteDistance`-Callable. */
interface TravelRouteLookupRequest {
  originAddress: string;
  destinationAddress: string;
}

/**
 * Mapped einen gefangenen Callable-Fehler (Firebase `FunctionsError`, `unknown` sonst) fail-
 * closed auf {@link TravelRouteLookupError}. Erkennt `details.code`/`details.field`, wie sie
 * `functions/src/travel/routeDistance.ts` über `HttpsError(..., { code, field? })` setzt.
 * Jeder unbekannte/fehlende `details.code` (z. B. `unauthenticated` ohne `details`, echter
 * Netzwerkausfall) fällt fail-closed auf `'network'`.
 */
function mapCallableErrorToLookupError(err: unknown): TravelRouteLookupError {
  const anyErr = err as { message?: unknown; details?: unknown } | null | undefined;
  const details = anyErr?.details as { code?: unknown; field?: unknown } | undefined;
  const rawCode = typeof details?.code === 'string' ? details.code : undefined;
  const code: TravelRouteLookupErrorCode =
    rawCode && (KNOWN_ROUTE_LOOKUP_ERROR_CODES as readonly string[]).includes(rawCode)
      ? (rawCode as TravelRouteLookupErrorCode)
      : 'network';
  const rawField = details?.field;
  const field: 'origin' | 'destination' | undefined =
    rawField === 'origin' || rawField === 'destination' ? rawField : undefined;
  const message = typeof anyErr?.message === 'string' && anyErr.message.length > 0 ? anyErr.message : 'Route lookup failed.';
  return new TravelRouteLookupError(code, message, field);
}

/**
 * Ruft die `travelRouteDistance`-Cloud-Function auf, um für zwei Adressen die Einfachstrecke
 * (Meter) zu ermitteln — ein ÜBERSCHREIBBARER VORSCHLAG (E15), NIE automatisch in `km`
 * übernommen (V22, Aufgabe des Aufrufers/`TripMileageForm`).
 *
 * Wirft IMMER eine {@link TravelRouteLookupError} (nie den rohen Firebase-Fehler) — der
 * Aufrufer kann `err.code` direkt für die `mileage.lookupErrors.<code>`-Texte verwenden.
 *
 * @param originAddress - Start-Adresse (bereits getrimmt erwartet, min. 3 Zeichen serverseitig).
 * @param destinationAddress - Ziel-Adresse (dieselben Regeln).
 * @throws {@link TravelRouteLookupError}
 */
export async function lookupRouteDistance(
  originAddress: string,
  destinationAddress: string,
): Promise<TravelRouteLookupResult> {
  try {
    const callable = httpsCallable<TravelRouteLookupRequest, TravelRouteLookupResult>(
      functions,
      'travelRouteDistance',
    );
    const res = await callable({ originAddress, destinationAddress });
    return res.data;
  } catch (err) {
    throw mapCallableErrorToLookupError(err);
  }
}
